import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Reveal } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { getDonationSession } from "@/lib/api";
import { CheckCircle2, Loader2, HeartHandshake, ArrowRight, AlertCircle } from "lucide-react";

export default function DonateSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [state, setState] = useState({ status: "loading", data: null, error: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", data: null, error: "Missing session id." });
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 10;

    async function poll() {
      attempts += 1;
      try {
        const data = await getDonationSession(sessionId);
        if (cancelled) return;
        if (data.status === "completed") {
          setState({ status: "completed", data, error: null });
          return;
        }
        if (attempts >= maxAttempts) {
          setState({ status: "pending", data, error: null });
          return;
        }
        setTimeout(poll, 1500);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", data: null, error: e?.response?.data?.detail || e.message || "Unable to verify session." });
      }
    }

    setState({ status: "loading", data: null, error: null });
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const centsToDollars = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 brp-hero-glow" />
        <div aria-hidden className="absolute inset-0 brp-hero-glow-orange opacity-60" />
        <div className="relative mx-auto w-full max-w-3xl px-5 sm:px-8 lg:px-10 py-24 sm:py-32 text-center">
          {state.status === "loading" && (
            <div>
              <Loader2 className="mx-auto h-10 w-10 text-[hsl(var(--brp-blue))] animate-spin" />
              <p className="mt-6 text-[hsl(var(--brp-ink-dim))]">Verifying your contribution…</p>
            </div>
          )}

          {state.status === "completed" && (
            <Reveal>
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-500/15 text-emerald-400 grid place-items-center">
                <CheckCircle2 className="h-8 w-8" />
              </div>
              <div className="mt-6 text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-orange))]">Contribution Received</div>
              <h1 data-testid="donate-success-heading" className="mt-4 font-display text-3xl sm:text-5xl font-semibold tracking-tight leading-tight">
                Thank you for investing in the next generation of founders.
              </h1>
              <p className="mt-6 text-[hsl(var(--brp-ink-dim))] leading-relaxed max-w-xl mx-auto">
                Your support helps power Legacy League’s work — expanding access to entrepreneurship, opportunity, and innovation for future generations.
              </p>
              <div className="mt-8 inline-flex items-center gap-3 rounded-xl border border-border/70 bg-[hsl(var(--brp-surface-2))] px-5 py-4 text-left">
                <HeartHandshake className="h-5 w-5 text-[hsl(var(--brp-blue))] shrink-0" />
                <div>
                  <div className="text-[10px] tracking-[0.28em] uppercase text-[hsl(var(--brp-ink-dim))]">Your contribution</div>
                  <div className="font-display text-xl font-semibold">
                    {centsToDollars(state.data.amount_cents)}
                    {state.data.cadence === "monthly" && <span className="text-sm text-[hsl(var(--brp-ink-dim))]"> / month</span>}
                  </div>
                </div>
              </div>
              <p className="mt-8 text-sm text-[hsl(var(--brp-ink-dim))]">A receipt has been emailed to you by Stripe.</p>
              <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link to="/legacy-league">
                  <Button variant="outline" className="rounded-full border-border/70 px-6">Back to Legacy League</Button>
                </Link>
                <Link to="/">
                  <Button className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-6">
                    Return Home <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </Reveal>
          )}

          {state.status === "pending" && (
            <Reveal>
              <div className="mx-auto h-16 w-16 rounded-full bg-[hsl(var(--brp-blue))]/15 text-[hsl(var(--brp-blue))] grid place-items-center">
                <Loader2 className="h-7 w-7 animate-spin" />
              </div>
              <h1 className="mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tight">Just a moment…</h1>
              <p className="mt-4 text-[hsl(var(--brp-ink-dim))] max-w-lg mx-auto">
                Your payment is still being confirmed. You can safely close this page — we’ll process the contribution as soon as it clears.
              </p>
            </Reveal>
          )}

          {state.status === "error" && (
            <Reveal>
              <div className="mx-auto h-16 w-16 rounded-full bg-red-500/15 text-red-400 grid place-items-center">
                <AlertCircle className="h-7 w-7" />
              </div>
              <h1 className="mt-6 font-display text-3xl sm:text-4xl font-semibold tracking-tight">We couldn’t verify this session.</h1>
              <p className="mt-4 text-[hsl(var(--brp-ink-dim))] max-w-lg mx-auto">{state.error}</p>
              <div className="mt-8">
                <Link to="/donate">
                  <Button className="rounded-full bg-[hsl(var(--brp-blue))] hover:bg-[#2563EB] text-white px-6">Return to Donation</Button>
                </Link>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
